import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import {
  BufferAttribute,
  BufferGeometry,
  Matrix4,
  type Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from "three";
import { MiterType } from "../enum/MiterType";

function flipShapeGeometry(shapeGeometry: ShapeGeometry) {
  const flipGeom = shapeGeometry.clone();
  const posAttr = flipGeom.getAttribute("position");
  if (!(posAttr instanceof BufferAttribute)) {
    throw new TypeError("flipShapeGeometry: expected position BufferAttribute");
  }
  for (let i = 0; i < posAttr.count; i++) {
    posAttr.setX(i, posAttr.getX(i) * -1);
  }
  posAttr.needsUpdate = true;

  const indexAttr = flipGeom.getIndex();
  if (!indexAttr) {
    throw new Error("flipShapeGeometry: expected indexed geometry");
  }
  const index = indexAttr.array as Uint32Array | Uint16Array;
  for (let i = 0; i < index.length; i += 3) {
    const a = index[i + 1];
    const b = index[i + 2];
    if (a === undefined || b === undefined) continue;
    index[i + 1] = b;
    index[i + 2] = a;
  }
  flipGeom.computeVertexNormals();
  return flipGeom;
}
export class ProfiledContourGeometry extends BufferGeometry {
  constructor(
    profileShape: Shape,
    contour: Vector2[],
    miterType: MiterType = MiterType.NONE,
    ccw = true,
  ) {
    super();
    const shapeBufferGeometry = new ShapeGeometry(profileShape);
    const flipProfileGeometry = flipShapeGeometry(shapeBufferGeometry);
    shapeBufferGeometry.rotateX(Math.PI * 0.5);
    const profileAttr = shapeBufferGeometry.getAttribute("position");
    if (!(profileAttr instanceof BufferAttribute)) {
      throw new TypeError(
        "ProfiledContourGeometry: expected profile position attribute",
      );
    }
    const profile = profileAttr;

    const profilePoints = new Float32Array(
      profile.count * (contour.length + 2) * 3,
    );

    const endProfiles: BufferAttribute[] = [];
    const texCoords: number[] = [];
    const points: Vector2[] = profileShape.extractPoints(2).shape;
    const maxY: number = (maxBy(points, "y") ?? { y: 0 }).y;
    const minY: number = (minBy(points, "y") ?? { y: 0 }).y;

    this.processContourPoints({
      contour,
      profile,
      points,
      profilePoints,
      endProfiles,
      texCoords,
      minY,
      maxY,
      miterType,
      ccw,
    });

    this.processEndProfiles(
      endProfiles,
      points,
      profilePoints,
      texCoords,
      contour.length,
      minY,
      maxY,
    );

    const originalPositions = new BufferAttribute(profilePoints, 3);
    const originalUvs = new BufferAttribute(new Float32Array(texCoords), 2);

    const index = this.generateIndices(
      contour,
      profile.count,
      flipProfileGeometry,
      shapeBufferGeometry,
    );

    this.applyFlatShading(index, originalPositions, originalUvs);
  }

  private processContourPoints(options: {
    contour: Vector2[];
    profile: BufferAttribute;
    points: Vector2[];
    profilePoints: Float32Array;
    endProfiles: BufferAttribute[];
    texCoords: number[];
    minY: number;
    maxY: number;
    miterType: MiterType;
    ccw: boolean;
  }): void {
    const {
      contour,
      profile,
      points,
      profilePoints,
      endProfiles,
      texCoords,
      minY,
      maxY,
      miterType,
      ccw,
    } = options;
    for (let i = 0; i < contour.length; i++) {
      const { v1, v2 } = this.calculateVectors(contour, i);
      const { hA, tA } = this.calculateAngles(v1, v2, i, contour.length);
      const matrices = this.buildMatrices({
        hA,
        tA,
        v1,
        v2,
        contourPoint: contour[i],
        index: i,
        contourLength: contour.length,
        miterType,
        ccw,
      });

      const cloneProfile = profile.clone();
      cloneProfile.applyMatrix4(matrices.shift);
      cloneProfile.applyMatrix4(matrices.rotation);
      cloneProfile.applyMatrix4(matrices.rotation2);
      cloneProfile.applyMatrix4(matrices.translation);

      this.addTextureCoords(points, texCoords, i, contour.length, minY, maxY);
      profilePoints.set(cloneProfile.array, cloneProfile.count * i * 3);

      if (i === 0 || i === contour.length - 1) {
        endProfiles.push(cloneProfile);
      }
    }
  }

  private calculateVectors(
    contour: Vector2[],
    i: number,
  ): { v1: Vector2; v2: Vector2 } {
    const prevIdx = i - 1 < 0 ? contour.length - 1 : i - 1;
    const nextIdx = i + 1 === contour.length ? 0 : i + 1;
    const v1 = new Vector2().subVectors(
      contour[prevIdx] ?? new Vector2(0, 0),
      contour[i] ?? new Vector2(0, 0),
    );
    const v2 = new Vector2().subVectors(
      contour[nextIdx] ?? new Vector2(0, 0),
      contour[i] ?? new Vector2(0, 0),
    );
    return { v1, v2 };
  }

  private calculateAngles(
    v1: Vector2,
    v2: Vector2,
    i: number,
    contourLength: number,
  ): { hA: number; tA: number } {
    const angle = v2.angle() - v1.angle();
    const halfAngle = angle * 0.5;

    let hA = halfAngle;
    let tA = v2.angle() + Math.PI * 0.5;

    if (i === 0 || i === contourLength - 1) {
      hA = Math.PI * 0.5;
    }
    if (i === contourLength - 1) {
      tA = v1.angle() - Math.PI * 0.5;
    }

    return { hA, tA };
  }

  private buildMatrices(options: {
    hA: number;
    tA: number;
    v1: Vector2;
    v2: Vector2;
    contourPoint: Vector2 | undefined;
    index: number;
    contourLength: number;
    miterType: MiterType;
    ccw: boolean;
  }): {
    shift: Matrix4;
    rotation: Matrix4;
    rotation2: Matrix4;
    translation: Matrix4;
  } {
    const {
      hA,
      tA,
      v1,
      v2,
      contourPoint,
      index,
      contourLength,
      miterType,
      ccw,
    } = options;
    const shift = Math.tan(hA - Math.PI * 0.5);
    const shiftMatrix = new Matrix4().set(
      1,
      0,
      0,
      0,
      -shift,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    const rotationMatrix = new Matrix4().set(
      Math.cos(tA),
      -Math.sin(tA),
      0,
      0,
      Math.sin(tA),
      Math.cos(tA),
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    const rotationMatrix2 = this.buildMiterRotationMatrix({
      index,
      contourLength,
      v1,
      v2,
      miterType,
      ccw,
    });

    const translationMatrix = new Matrix4().set(
      1,
      0,
      0,
      contourPoint?.x ?? 0,
      0,
      1,
      0,
      contourPoint?.y ?? 0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    return {
      shift: shiftMatrix,
      rotation: rotationMatrix,
      rotation2: rotationMatrix2,
      translation: translationMatrix,
    };
  }

  private buildMiterRotationMatrix(options: {
    index: number;
    contourLength: number;
    v1: Vector2;
    v2: Vector2;
    miterType: MiterType;
    ccw: boolean;
  }): Matrix4 {
    const { index, contourLength, v1, v2, miterType, ccw } = options;
    const rotationMatrix2 = new Matrix4().identity();

    const isStartMiter = this.isStartMiter(index, miterType, ccw);
    const isEndMiter = this.isEndMiter(index, contourLength, miterType, ccw);

    if (isStartMiter) {
      this.applyMiterRotation(rotationMatrix2, v2, ccw);
    }

    if (isEndMiter) {
      this.applyMiterRotation(rotationMatrix2, v1, ccw);
    }

    return rotationMatrix2;
  }

  private isStartMiter(
    index: number,
    miterType: MiterType,
    ccw: boolean,
  ): boolean {
    const miterAtStart = ccw ? MiterType.START : MiterType.END;
    return (
      index === 0 &&
      (miterType === MiterType.BOTH || miterType === miterAtStart)
    );
  }

  private isEndMiter(
    index: number,
    contourLength: number,
    miterType: MiterType,
    ccw: boolean,
  ): boolean {
    const miterAtEnd = ccw ? MiterType.END : MiterType.START;
    return (
      index === contourLength - 1 &&
      (miterType === MiterType.BOTH || miterType === miterAtEnd)
    );
  }

  private applyMiterRotation(
    matrix: Matrix4,
    vector: Vector2,
    ccw: boolean,
  ): void {
    if (vector.x === 0) {
      const shouldFlipY = vector.y < 0 !== ccw;
      matrix.elements[9] = shouldFlipY ? -1 : 1;
      return;
    }

    const shouldFlipX = vector.x < 0 !== ccw;
    matrix.elements[8] = shouldFlipX ? -1 : 1;
  }

  private addTextureCoords(
    points: Vector2[],
    texCoords: number[],
    i: number,
    contourLength: number,
    minY: number,
    maxY: number,
  ): void {
    for (const p of points) {
      texCoords.push((p.y - minY) / (maxY - minY));
      texCoords.push(i / contourLength);
    }
  }

  private processEndProfiles(
    endProfiles: BufferAttribute[],
    points: Vector2[],
    profilePoints: Float32Array,
    texCoords: number[],
    contourLength: number,
    minY: number,
    maxY: number,
  ): void {
    for (let idx = 0; idx < endProfiles.length; idx++) {
      const ep = endProfiles[idx];
      if (!ep) continue;
      for (const p of points) {
        texCoords.push((p.y - minY) / (maxY - minY), idx / contourLength);
      }
      profilePoints.set(ep.array, ep.count * (contourLength + idx) * 3);
    }
  }

  private generateIndices(
    contour: Vector2[],
    profileCount: number,
    flipProfileGeometry: ShapeGeometry,
    shapeBufferGeometry: ShapeGeometry,
  ): number[] {
    const index: number[] = [];
    const lastCorner = contour.length - 1;

    for (let i = 0; i < lastCorner; i++) {
      for (let j = 0; j < profileCount; j++) {
        const currCorner = i;
        const nextCorner = i + 1 === contour.length ? 0 : i + 1;
        const currPoint = j;
        const nextPoint = j + 1 === profileCount ? 0 : j + 1;

        const a = nextPoint + profileCount * currCorner;
        const b = currPoint + profileCount * currCorner;
        const c = currPoint + profileCount * nextCorner;
        const d = nextPoint + profileCount * nextCorner;

        index.push(a, b, d, b, c, d);
      }
    }

    const flipIdx = flipProfileGeometry.getIndex();
    const shapeIdx = shapeBufferGeometry.getIndex();
    if (!flipIdx || !shapeIdx) {
      throw new Error(
        "ProfiledContourGeometry: expected index buffers on profile geometries",
      );
    }
    for (const i of flipIdx.array) {
      index.push(i + profileCount * contour.length);
    }
    for (const i of Array.from(shapeIdx.array)) {
      index.push(i + profileCount * (contour.length + 1));
    }

    return index;
  }

  private applyFlatShading(
    index: number[],
    originalPositions: BufferAttribute,
    originalUvs: BufferAttribute,
  ): void {
    const numTriangles = index.length / 3;
    const numVertices = numTriangles * 3;

    const flatPositions = new Float32Array(numVertices * 3);
    const flatNormals = new Float32Array(numVertices * 3);
    const flatUvs = new Float32Array(numVertices * 2);

    const vA = new Vector3();
    const vB = new Vector3();
    const vC = new Vector3();
    const cb = new Vector3();
    const ab = new Vector3();
    const faceNormal = new Vector3();

    let posIdx = 0;
    let nrmIdx = 0;
    let uvIdx = 0;

    for (let i = 0; i < index.length; i += 3) {
      const idxA = index[i];
      const idxB = index[i + 1];
      const idxC = index[i + 2];
      if (idxA === undefined || idxB === undefined || idxC === undefined) {
        continue;
      }

      vA.fromBufferAttribute(originalPositions, idxA);
      vB.fromBufferAttribute(originalPositions, idxB);
      vC.fromBufferAttribute(originalPositions, idxC);

      cb.subVectors(vC, vB);
      ab.subVectors(vA, vB);
      faceNormal.crossVectors(cb, ab).normalize();

      vA.toArray(flatPositions, posIdx);
      posIdx += 3;
      vB.toArray(flatPositions, posIdx);
      posIdx += 3;
      vC.toArray(flatPositions, posIdx);
      posIdx += 3;

      for (let j = 0; j < 3; j++) {
        faceNormal.toArray(flatNormals, nrmIdx);
        nrmIdx += 3;
      }

      flatUvs[uvIdx++] = originalUvs.getX(idxA);
      flatUvs[uvIdx++] = originalUvs.getY(idxA);
      flatUvs[uvIdx++] = originalUvs.getX(idxB);
      flatUvs[uvIdx++] = originalUvs.getY(idxB);
      flatUvs[uvIdx++] = originalUvs.getX(idxC);
      flatUvs[uvIdx++] = originalUvs.getY(idxC);
    }

    this.setAttribute("position", new BufferAttribute(flatPositions, 3));
    this.setAttribute("normal", new BufferAttribute(flatNormals, 3));
    this.setAttribute("uv", new BufferAttribute(flatUvs, 2));
  }
}
